import { useEffect, useRef, useState } from 'react'
import { Ban, Check, CircleDashed, Loader2, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { duration } from './Ci'

/**
 * A run drawn as a graph, like GitHub's: a job sits one column right of the
 * last job it needs, and a line joins each job to what it needs. Used on a CI
 * run's page and in Push to prod, where Claude's merge-and-check step is the
 * first box.
 */
export type PipelineNode = {
  name: string
  needs: string[]
  /** A CI job status (waiting, queued, leased, success, failure, error, cancelled, skipped). */
  status: string
  startedAt: number | null
  finishedAt: number | null
  href?: string
}

const W = 210
const H = 34
const GAP_X = 56
const GAP_Y = 12

const RUNNING = new Set(['leased', 'running'])

function Icon({ status }: { status: string }) {
  if (status === 'success') return <Check size={14} className="shrink-0 text-ok" />
  if (status === 'failure' || status === 'error') return <X size={14} className="shrink-0 text-err" />
  if (status === 'cancelled' || status === 'skipped') return <Ban size={13} className="shrink-0 text-fg-faint" />
  if (RUNNING.has(status)) return <Loader2 size={14} className="shrink-0 animate-spin text-warn" />
  return <CircleDashed size={14} className="shrink-0 text-fg-faint" />
}

/** Ticks once a second while anything runs, so durations count up. */
function useNow(on: boolean): number {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!on) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [on])
  return now
}

export function PipelineGraph({ nodes }: { nodes: PipelineNode[] }) {
  const now = useNow(nodes.some((n) => RUNNING.has(n.status)))
  const byName = new Map(nodes.map((n) => [n.name, n]))
  const depth = new Map<string, number>()
  const depthOf = (n: PipelineNode, seen = new Set<string>()): number => {
    const d = depth.get(n.name)
    if (d !== undefined) return d
    if (seen.has(n.name)) return 0 // a cycle never passes the parser; this only stops a loop
    seen.add(n.name)
    const needs = n.needs.map((x) => byName.get(x)).filter((x): x is PipelineNode => !!x)
    const v = needs.length ? 1 + Math.max(...needs.map((x) => depthOf(x, seen))) : 0
    depth.set(n.name, v)
    return v
  }
  const cols: PipelineNode[][] = []
  for (const n of nodes) (cols[depthOf(n)] ??= []).push(n)
  const pos = new Map<string, { x: number; y: number }>()
  cols.forEach((col, c) => col.forEach((n, r) => pos.set(n.name, { x: c * (W + GAP_X), y: r * (H + GAP_Y) })))
  const width = cols.length * (W + GAP_X) - GAP_X
  const height = Math.max(...cols.map((c) => c.length)) * (H + GAP_Y) - GAP_Y
  // Keep the running stage in view: scroll to the leftmost running job, or
  // else the first that has not finished, whenever that changes.
  const box = useRef<HTMLDivElement>(null)
  const focus = nodes.filter((n) => RUNNING.has(n.status)).map((n) => pos.get(n.name)!.x)
  const target = focus.length ? Math.min(...focus) : null
  useEffect(() => {
    const el = box.current
    if (!el || target == null) return
    el.scrollTo({ left: Math.max(0, target - (el.clientWidth - W) / 2), behavior: 'smooth' })
  }, [target])

  return (
    <div ref={box} className="overflow-x-auto pb-2">
      <div className="relative" style={{ width, height }}>
        <svg className="absolute inset-0 text-fg-faint" width={width} height={height} fill="none">
          {nodes.flatMap((n) => n.needs.map((need) => {
            const a = pos.get(need)
            const b = pos.get(n.name)
            if (!a || !b) return null
            const x1 = a.x + W
            const y1 = a.y + H / 2
            const x2 = b.x
            const y2 = b.y + H / 2
            const mx = (x1 + x2) / 2
            return <path key={`${need}>${n.name}`} d={`M${x1} ${y1} C${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`} stroke="currentColor" strokeWidth={1.5} />
          }))}
        </svg>
        {nodes.map((n) => {
          const p = pos.get(n.name)!
          const Tag = n.href ? 'a' : 'div'
          return (
            <Tag key={n.name} href={n.href} title={n.name}
              className={cn('absolute flex items-center gap-2 rounded-lg border bg-surface-2 px-2.5 text-xs',
                n.status === 'failure' || n.status === 'error' ? 'border-err/50' : RUNNING.has(n.status) ? 'border-warn/50' : 'border-border',
                n.href && 'hover:bg-hover')}
              style={{ left: p.x, top: p.y, width: W, height: H }}>
              <Icon status={n.status} />
              <span className="flex-1 truncate">{n.name}</span>
              {n.startedAt && <span className="shrink-0 text-2xs text-fg-faint tabular-nums">{duration(n.startedAt, n.finishedAt, now)}</span>}
            </Tag>
          )
        })}
      </div>
    </div>
  )
}
